import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Form, Input, Select, Typography } from 'antd';
import { CopyOutlined, UserOutlined } from '@ant-design/icons';
import PhoneInput from '../components/PhoneInput';
import {
    DefaultValueProvider, FixedValueProvider, FormFieldPath, requireEtatCountries, useFormPath,
    applyPersonCopy, buildPersonCopyValues, collectPersonBlocks, looksLikeSamePerson, personLabel,
} from './index';
import COUNTRY from 'country-json/src/country-by-abbreviation.json';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

const { Text } = Typography;

export default (formPlugin, namespace) => {

    return {
        Renderer: (props) => {
            const { listFieldProps, buildName, path, absolutePath, storePath } = useFormPath()
            const { data } = props

            // Index d'occurrence (clé pour extraData par occurrence) = dernier
            // segment numérique du path (posé par productInfo). null hors Form.List.
            const listFieldName = [...path].reverse().find(seg => typeof seg === "number") ?? null
            const nodeExtraData = formPlugin.getExtraData(props.nodeId, listFieldName)
            // « Copié depuis Moi-même » (par occurrence, mémorisé dans extraData pour
            // survivre aux remontages). Sert à lever la règle « email/tél ≠ le vôtre »
            // et à masquer la relation tant que le bloc ressemble à l'identité.
            const [copyIdentity, setCopyIdentity] = useState(nodeExtraData?.copyIdentity || false)

            useEffect(() => {
                if (nodeExtraData?.copyIdentity !== undefined && nodeExtraData?.copyIdentity !== copyIdentity) {
                    setCopyIdentity(nodeExtraData?.copyIdentity)
                }
            }, [nodeExtraData?.copyIdentity])

            if (props.remove) {
                formPlugin.onAddFormPlugin(props, props.nodeId, props.pluginConfig.id)
            }

            const reducedWidth = data.reduced ? "48%" : "100%"
            const finalName = "[CONTACT]_" + data.fieldKey
            const withRelations = formPlugin.formData?.withRelations || false

            // Helper : produire le name complet d'un sous-champ
            const subName = (field) => buildName([finalName, field])

            // On étend les DEUX chemins avec ce contact (même segment). path pour
            // les names Antd des enfants, absolutePath pour la navigation formValues
            // (conditions imbriquées dans le contact).
            const childContext = useMemo(() => ({
                path: [...path, finalName],
                absolutePath: [...absolutePath, finalName],
                storePath: [...storePath, finalName],
                listFieldProps: null,
            }), [path, absolutePath, storePath, finalName])

            // Chemin ABSOLU de CE bloc dans le store du Form racine : cible des
            // écritures « Copier depuis… » et exclusion de soi-même des candidats.
            const selfStorePath = childContext.storePath

            const hasFixedGenre = data.fixedGenre === "Homme" || data.fixedGenre === "Femme"
            const hasFixedRelation = formPlugin.RELATIONS_OPTIONS.some(i => i.value === data.fixedRelation)

            // Règle « ≠ le vôtre » (email/tél) en forme de fonction antd : `getFieldValue`
            // est celui du Form RACINE, valable aussi dans un bloc de formulaire (où
            // formPlugin.formRef est nul). Levée uniquement si le bloc EST encore
            // l'identité (copie « Moi-même » autorisée par l'org, pas encore modifiée) —
            // jamais via un drapeau figé : dès que le visiteur retape une autre
            // personne en gardant l'email/tél de l'identité, la règle se réarme.
            const notOwnRule = (field, message) => ({ getFieldValue }) => ({
                validator: (_, _value) => {
                    const identity = getFieldValue("identity")
                    const own = identity?.[field]
                    if (!own || !_value) return Promise.resolve()
                    if (String(_value).toLowerCase() !== String(own).toLowerCase()) return Promise.resolve()
                    if (data.allowCopyIdentity && looksLikeSamePerson(getFieldValue(selfStorePath), identity)) return Promise.resolve()
                    return Promise.reject(new Error(message))
                }
            })

            // Après TOUS les hooks (React exige un nombre de hooks constant entre deux
            // rendus ; en éditeur admin, isPreviewMode bascule sur la cellule montée).
            if ((props.isPreviewMode || props.readOnly) && !props.data.fieldKey) {
                return null
            }

            // Détermine le pays/code utilisé pour le rendu conditionnel postal/etat
            const resolveCountry = (form) => {
                let pays = form.getFieldValue([...selfStorePath, "pays"])
                if (!pays) {
                    pays = COUNTRY.find(i => i.abbreviation === formPlugin.getCountry())?.country
                }
                const paysCode = COUNTRY.find(i => i.country === pays)?.abbreviation || formPlugin.getCountry()
                return { pays, paysCode }
            }

            return (
                <div>

                    {/* « Copier depuis… » : préremplissage PONCTUEL du bloc depuis
                        l'identité (« Moi-même », si l'org l'autorise) ou depuis un autre
                        bloc contact déjà saisi (racine, autre produit, autre occurrence,
                        bloc de formulaire). Les champs restent modifiables. Render-prop
                        shouldUpdate : la liste suit la saisie, et `_form` est l'instance
                        du Form RACINE (chemins absolus), même dans un bloc de formulaire.
                        Masqué tant qu'aucune personne n'est proposable. */}
                    <Form.Item noStyle shouldUpdate>
                        {(_form) => {
                            // Store RACINE lu par référence (aucun clone à chaque frappe) : même
                            // source que la règle « ≠ le vôtre », donc « Moi-même » et la règle
                            // voient la même identité. collectPersonBlocks écarte les occurrences
                            // au-delà de la quantité ; `disableCopyPerson` = interrupteur de l'org.
                            const allValues = _form.getFieldsValue(true)
                            const identityLabel = data.allowCopyIdentity ? personLabel(allValues?.identity) : ""
                            const others = data.disableCopyPerson ? [] : collectPersonBlocks(allValues, "CONTACT", selfStorePath)
                            if (!identityLabel && !others.length) return null

                            const blockKey = (o) => "block:" + JSON.stringify(o.storePath)
                            const items = []
                            if (identityLabel) {
                                items.push({ key: "identity", label: t`Moi-même` + " (" + identityLabel + ")" })
                                if (others.length) items.push({ type: "divider" })
                            }
                            others.forEach(o => items.push({ key: blockKey(o), label: o.label }))

                            const onClick = ({ key }) => {
                                const fromIdentity = key === "identity"
                                const entry = fromIdentity ? null : others.find(o => blockKey(o) === key)
                                // Entrée introuvable (liste re-rendue entre l'ouverture et le clic) :
                                // ne rien faire, surtout pas lire un chemin vide (= tout le store).
                                if (!fromIdentity && !entry) return
                                // Relecture au clic (jamais la valeur capturée au rendu).
                                const source = _form.getFieldValue(fromIdentity ? "identity" : entry.storePath)
                                const values = buildPersonCopyValues(source, {
                                    fromIdentity,
                                    fixedGenre: hasFixedGenre,
                                    fixedRelation: hasFixedRelation,
                                })
                                applyPersonCopy(_form, formPlugin, selfStorePath, values, {
                                    keepKeys: [hasFixedGenre && "homme", hasFixedRelation && "_relation"].filter(Boolean),
                                })
                                setCopyIdentity(fromIdentity)
                                formPlugin.registerExtraData(props.nodeId, { copyIdentity: fromIdentity }, listFieldName)
                            }

                            return (
                                <Form.Item>
                                    <Dropdown menu={{ items, onClick }} trigger={["click"]}>
                                        <Button size="small" icon={<CopyOutlined />}>{t`Copier depuis…`}</Button>
                                    </Dropdown>
                                </Form.Item>
                            )
                        }}
                    </Form.Item>

                    {(() => {
                        let _defaultValue = undefined
                        let fixedGenre = false
                        if (data.fixedGenre === "Homme" || data.fixedGenre === "Femme") {
                            fixedGenre = true
                            _defaultValue = data.fixedGenre === "Homme"
                        }
                        return (
                            <Form.Item
                                {...(listFieldProps || {})}
                                label={t`Civilité` + " :"}
                                name={subName("homme")}
                                rules={[{
                                    required: data.required,
                                    message: t`Ce champ est obligatoire`
                                }]}
                            >
                                <FixedValueProvider defaultValue={_defaultValue}>
                                    <Select disabled={fixedGenre} size="large" placeholder={t`Civilité`}>
                                        <Select.Option value={true}><Trans>Monsieur</Trans></Select.Option>
                                        <Select.Option value={false}><Trans>Madame</Trans></Select.Option>
                                    </Select>
                                </FixedValueProvider>
                            </Form.Item>
                        )
                    })()}

                    {withRelations && (
                        <Form.Item noStyle shouldUpdate>
                            {(_form) => {
                                const identity = _form.getFieldValue("identity")
                                // Copié depuis « Moi-même » et pas encore modifié → pas de
                                // relation (une personne n'est pas en relation avec elle-même).
                                // Dès que la saisie diverge de l'identité, le sélecteur revient.
                                if (copyIdentity && looksLikeSamePerson(_form.getFieldValue(selfStorePath), identity)) {
                                    return null
                                }
                                const homme = _form.getFieldValue(subName("homme"))

                                let label = t`Il/elle est`
                                if (homme === true) label = t`Il est`
                                if (homme === false) label = t`Elle est`

                                const options = formPlugin.RELATIONS_OPTIONS

                                let _defaultValue = undefined
                                let fixedRelation = false
                                if (options.some(i => i.value === data.fixedRelation)) {
                                    fixedRelation = true
                                    _defaultValue = data.fixedRelation
                                }

                                const _name = identity?.prenom && identity?.nom ? `${identity.prenom} ${identity.nom}` : null

                                return (
                                    <Form.Item
                                        {...(listFieldProps || {})}
                                        label={label + " :"}
                                        name={subName("_relation")}
                                        rules={[{
                                            // Bloc copié depuis « Moi-même » : s'il réapparaît parce que le
                                            // visiteur a corrigé SON identité, il ne doit pas bloquer la
                                            // soumission (aucune relation avec soi-même n'existe).
                                            required: data.required && !copyIdentity,
                                            message: t`Ce champ est obligatoire`
                                        }]}
                                    >
                                        <FixedValueProvider defaultValue={_defaultValue}>
                                            <Select disabled={fixedRelation} size="large" placeholder={label}>
                                                {options.map(i => {
                                                    let _label = i.labelH + " / " + i.labelF
                                                    if (homme === true) _label = i.labelH
                                                    if (homme === false) _label = i.labelF

                                                    return (
                                                        <Select.Option key={i.value} value={i.value}>
                                                            {_name ? (
                                                            <Trans context="form.family-relationship" comment="Selected family relationship of the person being entered to the named contact; the name is a literal proper name. Translate as a complete phrase and reorder if needed.">{_label} de <Text strong>{_name}</Text></Trans>
                                                        ) : _label}
                                                        </Select.Option>
                                                    )
                                                })}
                                                <Select.Option key="OTHER" value="OTHER"><Trans>Autre</Trans></Select.Option>
                                            </Select>
                                        </FixedValueProvider>
                                    </Form.Item>
                                )
                            }}
                        </Form.Item>
                    )}

                    <div style={{ display: data.reduced ? "flex" : "block", justifyContent: "space-between" }}>
                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelPrenom || t`Prénom`) + " :"}
                            name={subName("prenom")}
                            rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                            style={{ width: reducedWidth }}
                        >
                            <Input type="text" placeholder={data.labelPrenom || t`Prénom`} />
                        </Form.Item>

                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelNom || t`Nom`) + " :"}
                            name={subName("nom")}
                            rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                            style={{ width: reducedWidth }}
                        >
                            <Input type="text" placeholder={data.labelNom || t`Nom`} />
                        </Form.Item>
                    </div>

                    <div style={{
                        display: (data.reduced && props.device === "DESKTOP") ? "flex" : "block",
                        justifyContent: "space-between"
                    }}>
                        {data.ignoreEmail !== true && (
                            <Form.Item
                                {...(listFieldProps || {})}
                                label={(data.labelEmail || t`Email`) + " :"}
                                name={subName("email")}
                                rules={[{
                                    required: data.required,
                                    message: t`Ce champ est obligatoire`
                                }, {
                                    type: "email",
                                    message: t`L'adresse email n'est pas valide`
                                }, notOwnRule("email", t`L'adresse email ne doit pas être la même que la vôtre`)]}
                                style={{ width: (data.reduced && props.device === "DESKTOP") ? reducedWidth : "100%" }}
                            >
                                <Input type="email" placeholder={data.labelEmail || t`Email`} />
                            </Form.Item>
                        )}

                        <Form.Item
                            {...(listFieldProps || {})}
                            label={(data.labelTel || t`Téléphone`) + " :"}
                            name={subName("tel")}
                            rules={[{
                                required: data.required,
                                message: t`Ce champ est obligatoire`
                            }, {
                                validator: (_, _value) => {
                                    if (!_value) return Promise.resolve()
                                    const cleaned = String(_value).replace(/[\s\-().]/g, "")
                                    return /^\+?[0-9]{6,20}$/.test(cleaned)
                                        ? Promise.resolve()
                                        : Promise.reject(new Error(t`Le numéro de téléphone est invalide`))
                                }
                            }, notOwnRule("tel", t`Le numéro de téléphone ne doit pas être le même que le vôtre`)]}
                            style={{ width: (data.reduced && props.device === "DESKTOP") ? reducedWidth : "100%" }}
                        >
                            <PhoneInput defaultLang={props.lang} placeholder={data.labelTel || t`Téléphone`} />
                        </Form.Item>
                    </div>

                    {data.full && (
                        <Fragment>
                            <Form.Item
                                {...(listFieldProps || {})}
                                label={(data.labelAdresse || t`Adresse`) + " :"}
                                name={subName("adresse")}
                                rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                            >
                                <Input type="text" placeholder={data.labelAdresse || t`Adresse`} />
                            </Form.Item>

                            <div style={{ display: data.reduced ? "flex" : "block", justifyContent: "space-between" }}>

                                <Form.Item noStyle shouldUpdate>
                                    {(_form) => {
                                        const { paysCode } = resolveCountry(_form)
                                        if ("IL" === paysCode) return null

                                        return (
                                            <Form.Item
                                                {...(listFieldProps || {})}
                                                label={(data.labelPostal || t`Code Postal`) + " :"}
                                                name={subName("postal")}
                                                rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                                                style={{ width: reducedWidth }}
                                            >
                                                <Input type="text" placeholder={data.labelPostal || t`Code Postal`} />
                                            </Form.Item>
                                        )
                                    }}
                                </Form.Item>

                                <Form.Item
                                    {...(listFieldProps || {})}
                                    label={(data.labelVille || t`Ville`) + " :"}
                                    name={subName("ville")}
                                    rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                                    style={{ width: reducedWidth }}
                                >
                                    <Input type="text" placeholder={data.labelVille || t`Ville`} />
                                </Form.Item>
                            </div>

                            <Form.Item noStyle shouldUpdate>
                                {(_form) => {
                                    const { paysCode } = resolveCountry(_form)
                                    if (!requireEtatCountries.includes(paysCode)) return null

                                    return (
                                        <Form.Item
                                            {...(listFieldProps || {})}
                                            label={t`État / Province` + " :"}
                                            name={subName("etat")}
                                            rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                                        >
                                            <Input type="text" placeholder={data.labelEtat || t`État / Province`} autoComplete="state" />
                                        </Form.Item>
                                    )
                                }}
                            </Form.Item>

                            <Form.Item
                                {...(listFieldProps || {})}
                                label={t`Pays` + " :"}
                                name={subName("pays")}
                                rules={[{ required: data.required, message: t`Ce champ est obligatoire` }]}
                            >
                                <DefaultValueProvider defaultValue={COUNTRY.find(i => i.abbreviation === formPlugin.getCountry())?.country}>
                                    <select className="form-control">
                                        <option unselectable disabled><Trans>Pays</Trans></option>
                                        {COUNTRY.map(i => (
                                            <option key={i.abbreviation} value={i.country}>{i.country}</option>
                                        ))}
                                    </select>
                                </DefaultValueProvider>
                            </Form.Item>

                            {data.showNote && (
                                <Form.Item
                                    {...(listFieldProps || {})}
                                    label={(data.labelNote || t`Complément d'adresse`) + " :"}
                                    name={subName("note_adresse")}
                                >
                                    <Input placeholder={data.labelNote || t`Complément d'adresse`} />
                                </Form.Item>
                            )}
                        </Fragment>
                    )}

                    <FormFieldPath.Provider value={childContext}>
                        {props.children}
                    </FormFieldPath.Provider>
                </div>
            )
        },
        namespace: namespace,
        id: 'form-contact' + namespace,
        title: t`Champ Contact`,
        description: t`Élément de formulaire utilisable.`,
        icon: <UserOutlined />,
        iconColor: "#F39C12",
        version: 1,
        cellPlugins: (plugins, data) => {
            const initialPlugins = formPlugin.getPluginsMem(namespace).filter(i =>
                !i.id.includes("form-contact") &&
                !i.id.includes("form-child") &&
                !i.id.includes("form-hazkara") &&
                !i.id.includes("form-identity") &&
                !i.id.includes("form-product") &&
                !i.id.includes("form-step-container") &&
                !i.id.includes("form-submit-button") &&
                !i.id.includes("form-free-amount") &&
                !i.id.includes("form-paiement-method") &&
                !i.id.includes("form-calendar")
            )
            return [...initialPlugins, ...formPlugin.getContactFormPlugins(namespace)]
        },
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    required: {
                        type: 'boolean',
                        uniforms: { label: t`Obligatoire` }
                    },
                    full: {
                        type: 'boolean',
                        uniforms: { label: t`Contact Complet` }
                    },
                    reduced: {
                        type: 'boolean',
                        uniforms: { label: t`Formulaire Réduit` }
                    },
                    allowCopyIdentity: {
                        type: 'boolean',
                        uniforms: { label: t`Permettre la copie des informations identitaires` }
                    },
                    disableCopyPerson: {
                        type: 'boolean',
                        uniforms: { label: t`Désactiver la copie depuis une autre personne` }
                    },
                    fieldKey: {
                        type: 'string',
                        uniforms: {
                            required: true,
                            label: t`Identifiant`,
                            placeholder: t`Identifiant du Champ`
                        }
                    },
                    labelNom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Nom`,
                            placeholder: t`Étiquette Nom`
                        }
                    },
                    labelPrenom: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Prénom`,
                            placeholder: t`Étiquette Prénom`
                        }
                    },
                    labelEmail: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Email`,
                            placeholder: t`Étiquette Email`
                        }
                    },
                    ignoreEmail: {
                        type: 'boolean',
                        uniforms: { label: t`Masquer l'email` }
                    },
                    labelTel: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Téléphone`,
                            placeholder: t`Étiquette Téléphone`
                        }
                    },
                    labelAdresse: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Adresse`,
                            placeholder: t`Étiquette Adresse`,
                            showIf: (data) => data.full
                        }
                    },
                    labelPostal: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Postal`,
                            placeholder: t`Étiquette Postal`,
                            showIf: (data) => data.full
                        }
                    },
                    labelVille: {
                        type: 'string',
                        uniforms: {
                            label: t`Étiquette Ville`,
                            placeholder: t`Étiquette Ville`,
                            showIf: (data) => data.full
                        }
                    },
                    showNote: {
                        type: 'boolean',
                        uniforms: { label: t`Afficher le Complément d'adresse` }
                    },
                    fixedGenre: {
                        type: 'string',
                        uniforms: {
                            label: t`Fixer le genre`,
                            allowedValues: ["Homme", "Femme"],
                            transform: (value) => {
                                if(value==="Homme"){
                                    return t`Homme`
                                }else{
                                    return t`Femme`
                                }
                            },
                        }
                    },
                    fixedRelation: {
                        type: 'string',
                        uniforms: {
                            label: t`Fixer la relation`,
                            allowedValues: formPlugin.RELATIONS_OPTIONS.map(i=>i.value),
                            transform: (value) => {
                                let opt=formPlugin.RELATIONS_OPTIONS.find(i=>i.value === value)
                                return opt.labelH+" / "+opt.labelF
                            },
                        }
                    },
                }
            }
        }
    }
}
